'use client'

// S1 Photos state machine — prototype postform.jsx:94-166 (PhotoField).
// idle dashed drop button → fake upload (thumbs pop in @140+i*150ms) →
// scanbeam scan (+250ms) → ready (~3.24s): AI-ordered hscroll + inline advice.
// Ready-state controls (user decisions 2026-07-15): tap = cover, drag =
// reorder, hover × = delete. Photos are never altered — advice only.
import * as React from 'react'
import { Copy, ImagePlus, Loader2, Sparkles, SunMedium, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'
import { DRAFT_PHOTOS } from './post-data'
import { PhotoThumb } from './widgets'
import type { DraftPhoto } from './post-data'

export type PhotoPhase = 'idle' | 'uploading' | 'scanning' | 'ready'

export interface PhotoFieldState {
  phase: PhotoPhase
  n: number
  dupRemoved: boolean
  /** Explicit id order — position 0 IS the cover; null = the AI impact order. */
  order: string[] | null
  /** Photos the seller deleted from the set. */
  removed: string[]
  /** Seller-renamed room labels, by photo id. */
  labels: Record<string, string>
}

export const INITIAL_PHOTO_STATE: PhotoFieldState = {
  phase: 'idle',
  n: 0,
  dupRemoved: true,
  order: null,
  removed: [],
  labels: {},
}

/** Kept photos, cover first, renames applied — shared by the field, the draft
 * and the preview. */
export function orderedPhotos(
  s: Pick<PhotoFieldState, 'dupRemoved' | 'order' | 'removed' | 'labels'>
): DraftPhoto[] {
  const keep = DRAFT_PHOTOS.filter((p) => !(p.dup && s.dupRemoved) && !s.removed.includes(p.id))
  const named = keep.map((p) => (s.labels[p.id] ? { ...p, room: s.labels[p.id] } : p))
  if (!s.order) return [...named.filter((p) => p.cover), ...named.filter((p) => !p.cover)]
  const pos = new Map(s.order.map((id, i) => [id, i]))
  // Ids missing from the explicit order (e.g. a dup re-added later) go last,
  // keeping their dataset order (sort is stable).
  return [...named].sort((a, b) => (pos.get(a.id) ?? 1e9) - (pos.get(b.id) ?? 1e9))
}

export interface PhotoFieldProps {
  state: PhotoFieldState
  setState: React.Dispatch<React.SetStateAction<PhotoFieldState>>
}

export function PhotoField({ state, setState }: PhotoFieldProps) {
  const { t } = useI18n()
  const { phase, n, dupRemoved, removed } = state
  const timers = React.useRef<ReturnType<typeof setTimeout>[]>([])
  // State drives the dragged thumb's dimmed look; the ref is what drop logic
  // reads (dragstart → drop can land within one tick — state would lag).
  const [dragId, setDragId] = React.useState<string | null>(null)
  const dragIdRef = React.useRef<string | null>(null)
  React.useEffect(() => () => timers.current.forEach(clearTimeout), [])

  const start = () => {
    if (phase !== 'idle') return
    setState((s) => ({ ...s, phase: 'uploading' }))
    const at = (ms: number, fn: () => void) => timers.current.push(setTimeout(fn, ms))
    DRAFT_PHOTOS.forEach((_, i) => at(140 + i * 150, () => setState((s) => ({ ...s, n: i + 1 }))))
    at(140 + DRAFT_PHOTOS.length * 150 + 250, () => setState((s) => ({ ...s, phase: 'scanning' })))
    at(140 + DRAFT_PHOTOS.length * 150 + 1900, () => setState((s) => ({ ...s, phase: 'ready' })))
  }

  const ordered = React.useMemo(() => orderedPhotos(state), [state])

  // Drop the dragged photo at the dragged-over photo's position.
  const dropOn = (targetId: string) => {
    const sourceId = dragIdRef.current
    if (!sourceId || sourceId === targetId) return
    setState((s) => {
      const ids = orderedPhotos(s).map((p) => p.id)
      const from = ids.indexOf(sourceId)
      const to = ids.indexOf(targetId)
      if (from < 0 || to < 0) return s
      ids.splice(to, 0, ids.splice(from, 1)[0])
      return { ...s, order: ids }
    })
  }

  const removePhoto = (id: string) => setState((s) => ({ ...s, removed: [...s.removed, id] }))

  // Advice rows only make sense while their subject photo is still in the set.
  const darkPhoto = DRAFT_PHOTOS.find((p) => p.dark)
  const dupPhoto = DRAFT_PHOTOS.find((p) => p.dup)
  const showDarkAdvice = darkPhoto && !removed.includes(darkPhoto.id)
  const showDupAdvice = dupPhoto && !removed.includes(dupPhoto.id)

  if (phase === 'idle') {
    return (
      <button
        type="button"
        onClick={start}
        className="flex w-full flex-col items-center gap-2 rounded-shape-sm border-[1.5px] border-dashed border-outline bg-surface-low px-4 py-[26px] text-ink-secondary"
      >
        <ImagePlus size={30} className="text-ink-muted" />
        <span className="text-[13.5px] font-semibold">{t('post.photosDrop', 'Drop photos here, or browse')}</span>
        <span className="text-[11.5px] text-ink-muted">
          {t('post.photosDropHint', 'Any order, any quality — they get sorted, brightened and de-duplicated for you')}
        </span>
      </button>
    )
  }

  if (phase === 'uploading' || phase === 'scanning') {
    return (
      <div>
        <div className="relative flex flex-wrap gap-2 overflow-hidden rounded-[10px]">
          {DRAFT_PHOTOS.slice(0, n).map((p) => (
            <div key={p.id} className="animate-pop-in [animation-duration:0.25s]">
              <PhotoThumb photo={p} size={62} showLabel={false} />
            </div>
          ))}
          {phase === 'scanning' ? <div className="scanbeam" /> : null}
        </div>
        <div className="mt-[10px] flex items-center gap-2 text-[12px] text-ink-secondary">
          <Loader2 size={15} className="animate-spin text-primary" style={{ animationDuration: '0.9s' }} />
          {phase === 'uploading' ? (
            <span className="font-mono">
              {n} / {DRAFT_PHOTOS.length} {t('post.uploading', 'uploading…')}
            </span>
          ) : (
            t('post.scanning', 'Recognizing rooms, checking light and sharpness…')
          )}
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Wrapping rows (no horizontal scroll). Position 0 IS the cover —
          drag a photo there to promote it. Names are editable in place. */}
      <div className="flex flex-wrap gap-2 pb-[2px]">
        {ordered.map((p, i) => (
          <div
            key={p.id}
            className={cn(
              'group/thumb relative shrink-0 cursor-grab active:cursor-grabbing',
              dragId === p.id && 'opacity-40'
            )}
            title={
              i === 0
                ? t('post.isCover', 'Cover photo — the first position is the cover')
                : t('post.dragReorder', 'Drag to reorder — drop into first position to make it the cover')
            }
            draggable
            onDragStart={(e) => {
              dragIdRef.current = p.id
              setDragId(p.id)
              e.dataTransfer.effectAllowed = 'move'
              e.dataTransfer.setData('text/plain', p.id)
            }}
            onDragEnd={() => {
              dragIdRef.current = null
              setDragId(null)
            }}
            onDragOver={(e) => {
              e.preventDefault()
              e.dataTransfer.dropEffect = 'move'
            }}
            onDrop={(e) => {
              e.preventDefault()
              dropOn(p.id)
              dragIdRef.current = null
              setDragId(null)
            }}
          >
            <PhotoThumb photo={p} size={68} cover={i === 0} showLabel={false} />
            <input
              value={p.room}
              placeholder={t('post.roomNamePlaceholder', 'Room…')}
              aria-label={t('post.roomNameLabel', 'Photo name')}
              title={t('post.roomNameTitle', 'Click to rename this photo')}
              onChange={(e) => {
                const v = e.target.value
                setState((s) => ({ ...s, labels: { ...s.labels, [p.id]: v } }))
              }}
              onDragStart={(e) => {
                // Text-selection drags must not kick off a photo reorder.
                e.preventDefault()
                e.stopPropagation()
              }}
              className="mt-[3px] w-[68px] cursor-text rounded-[4px] border-none bg-transparent text-center text-[10px] text-ink-muted outline-none transition-colors placeholder:text-ink-muted/60 hover:bg-surface-low focus:bg-surface-low focus:text-ink"
            />
            {ordered.length > 1 ? (
              <button
                type="button"
                onClick={() => removePhoto(p.id)}
                title={t('post.removePhoto', 'Remove photo')}
                className="absolute right-1 top-1 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[rgba(26,23,23,0.7)] opacity-0 transition-opacity focus-visible:opacity-100 group-hover/thumb:opacity-100"
              >
                <X size={11} color="#fff" />
              </button>
            ) : null}
          </div>
        ))}
      </div>
      <div className="mt-[10px] flex flex-col gap-[6px]">
        {/* Photos are never altered — the assistant only ADVISES on what to
            improve and how (user decision 2026-07-15). */}
        {showDarkAdvice ? (
          <div className="flex items-center gap-2 rounded-shape-sm bg-surface-low px-[11px] py-2">
            <SunMedium size={15} className="shrink-0 text-warning" />
            <span className="flex-1 text-[12px] text-ink-secondary">
              <b className="font-semibold text-ink">{t('post.adviceDarkLead', 'Bedroom shot is dark.')}</b>{' '}
              {t(
                'post.adviceDarkBody',
                'Consider re-shooting by day or with every light on — bright photos get noticeably more views.'
              )}
            </span>
          </div>
        ) : null}
        {showDupAdvice ? (
          <div className="flex items-center gap-2 rounded-shape-sm bg-surface-low px-[11px] py-2">
            <Copy size={15} className="shrink-0 text-ink-muted" />
            <span className="flex-1 text-[12px] text-ink-secondary">
              {t('post.fixDupPrefix', 'Two near-identical living rooms —')}{' '}
              {dupRemoved ? t('post.fixDupOn', 'kept the sharper one') : t('post.fixDupOff', 'keeping both')}
            </span>
            <button
              type="button"
              onClick={() => setState((s) => ({ ...s, dupRemoved: !s.dupRemoved }))}
              className="shrink-0 text-[11.5px] font-bold text-primary"
            >
              {dupRemoved ? t('post.keepBoth', 'Keep both') : t('post.undo', 'Undo')}
            </button>
          </div>
        ) : null}
      </div>
      <div className="mt-2 flex items-center gap-[5px] text-[11px] text-ink-muted">
        <Sparkles size={12} className="text-primary" />
        {t(
          'post.photosFootnote',
          'The first photo is the cover — drag to reorder, × to remove, click a name to rename.'
        )}
      </div>
    </div>
  )
}
